/**
 * Recursively flattens a nested object into a single-level object with hyphenated keys.
 * Skips function values and only includes serializable values.
 * 
 * @example
 * flattenTheme({ palette: { primary: { main: '#fff' } } }) 
 * // Returns: { 'palette-primary-main': '#fff' }
 * 
 * @param {Object} obj - The nested theme object to flatten.
 * @param {string} [prefix=''] - Optional prefix for the keys.
 * @returns {Object.<string, string|number>} A flat object with hyphenated keys.
 */
export const flattenTheme = (obj, prefix = '') => {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    const newKey = prefix ? `${prefix}-${key}` : key;

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(acc, flattenTheme(value, newKey));
    } else if (typeof value !== 'function') {
      acc[newKey] = value;
    }

    return acc;
  }, {});
};

/**
 * Injects theme design tokens as CSS variables into a global <style> tag.
 * All variables are prefixed with '--ca-theme-' and applied to the :root selector.
 * 
 * @param {Object} theme - The design token / theme object from the host application.
 */
export function setTheme(theme) {
  if (typeof document === 'undefined') return;

  const flatTheme = flattenTheme(theme);
  const cssVariables = Object.entries(flatTheme)
    .map(([key, value]) => `  --ca-theme-${key}: ${value};`)
    .join('\n');

  const cssContent = `
    /* Styles from coolremote-shared theme injection */
    :root {\n${cssVariables}\n}
  `;

  let styleTag = document.getElementById('ca-dynamic-theme');
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = 'ca-dynamic-theme';
    styleTag.type = 'text/css';
    styleTag.setAttribute('data-origin', 'coolremote-shared');
    document.head.appendChild(styleTag);
  }

  styleTag.textContent = cssContent;
};
